import React from 'react';
import {
  Circle,
  INCH,
  Line,
  Rect,
  SchematicShell,
} from './SchematicPrimitives';

/**
 * Single-gang switch family — switch / dimmer / 3-way. All three share
 * the same plate; only the control glyph differs:
 *   - switch:  single horizontal rocker
 *   - dimmer:  rocker with a slider tick (small horizontal stub on the
 *              left edge of the rocker, indicating it slides)
 *   - 3way:    three stacked horizontal rockers (per the plan)
 *
 * Dispatch is by obj.type — the three are separate registry entries.
 * No additional variant fields read.
 *
 * Side views: tiny silhouette — a very thin (~1") wall plate flush to
 * the back of the bounding box, with a small protrusion at the front
 * center for the toggle. Plate spans the full height; toggle protrudes
 * ~3/8" from the front center. Symmetric (toggle is centered) so we
 * mirror the same drawing for left ↔ right.
 */

const PLATE_INSET_RATIO = 0.18;
const ROCKER_WIDTH_RATIO = 0.5;
const ROCKER_HEIGHT_RATIO = 0.55;

const PLATE_THICKNESS_M = 0.28 * INCH; // ~5/16" — covers wall + airgap
const TOGGLE_PROTRUSION_M = 0.375 * INCH; // ~3/8"

function plateAndRockerGeometry(width, height) {
  const insetX = width * PLATE_INSET_RATIO;
  const insetY = height * 0.06;
  const innerW = Math.max(0, width - 2 * insetX);
  const innerH = Math.max(0, height - 2 * insetY);
  const rockerW = innerW * ROCKER_WIDTH_RATIO;
  const rockerH = innerH * ROCKER_HEIGHT_RATIO;
  const rockerX = width / 2 - rockerW / 2;
  const rockerY = height / 2 - rockerH / 2;
  return { insetX, insetY, innerW, innerH, rockerW, rockerH, rockerX, rockerY };
}

export function SwitchSchematic(props) {
  return <SwitchFamily {...props} kind="switch" />;
}
SwitchSchematic.supportsSideViews = true;

export function DimmerSchematic(props) {
  return <SwitchFamily {...props} kind="dimmer" />;
}
DimmerSchematic.supportsSideViews = true;

export function ThreeWaySchematic(props) {
  return <SwitchFamily {...props} kind="3way" />;
}
ThreeWaySchematic.supportsSideViews = true;

function SwitchFamily({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
  kind,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SwitchSide
        depth={depth}
        height={height}
        kind={kind}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const g = plateAndRockerGeometry(width, height);
  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {kind === '3way' ? (
        <TripleRocker
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <SingleRocker
          rockerX={g.rockerX}
          rockerY={g.rockerY}
          rockerW={g.rockerW}
          rockerH={g.rockerH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          isDimmer={kind === 'dimmer'}
        />
      )}
    </SchematicShell>
  );
}

function SwitchSide({ depth, height, kind, strokeColor, strokeWidth }) {
  // Canonical orientation: back of plate at x = 0, front at x = depth.
  // The plate is a thin slab spanning the full height; the toggle
  // protrudes forward from the vertical center. Symmetric — mirror
  // is geometrically identical, so we render the same drawing for
  // both left and right.
  const plateW = Math.min(PLATE_THICKNESS_M, Math.max(0, depth * 0.4));
  const protrusion = Math.min(TOGGLE_PROTRUSION_M, Math.max(0, depth - plateW));
  // Toggle vertical extent: smaller area for 3way (three smaller
  // toggles stacked) than single-gang.
  const toggleH = kind === '3way' ? height * 0.55 : height * 0.25;
  const toggleY0 = height / 2 - toggleH / 2;
  const toggleY1 = height / 2 + toggleH / 2;
  return (
    <SchematicShell
      width={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Wall plate — thin slab against the back wall. */}
      <Rect
        x={0}
        y={0}
        width={plateW}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Toggle bump protruding forward from the plate. */}
      {protrusion > 0 && (
        <Rect
          x={plateW}
          y={toggleY0}
          width={protrusion}
          height={toggleY1 - toggleY0}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </SchematicShell>
  );
}

function SingleRocker({
  rockerX,
  rockerY,
  rockerW,
  rockerH,
  strokeColor,
  strokeWidth,
  isDimmer,
}) {
  return (
    <g>
      <Rect
        x={rockerX}
        y={rockerY}
        width={rockerW}
        height={rockerH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {isDimmer ? (
        <DimmerSliderGlyph
          rockerX={rockerX}
          rockerY={rockerY}
          rockerW={rockerW}
          rockerH={rockerH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <Line
          x1={rockerX}
          y1={rockerY + rockerH / 2}
          x2={rockerX + rockerW}
          y2={rockerY + rockerH / 2}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </g>
  );
}

/**
 * Slider glyph for dimmers — small filled dot on a vertical track up
 * the middle of the rocker. Distinguishes from the plain switch's
 * pivot line.
 */
function DimmerSliderGlyph({
  rockerX,
  rockerY,
  rockerW,
  rockerH,
  strokeColor,
  strokeWidth,
}) {
  const cx = rockerX + rockerW / 2;
  return (
    <g>
      <Line
        x1={cx}
        y1={rockerY + rockerH * 0.15}
        x2={cx}
        y2={rockerY + rockerH * 0.85}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Circle
        cx={cx}
        cy={rockerY + rockerH * 0.4}
        r={Math.min(rockerW, rockerH) * 0.12}
        fill={strokeColor}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function TripleRocker({ width, height, strokeColor, strokeWidth }) {
  const insetX = width * PLATE_INSET_RATIO;
  const innerW = Math.max(0, width - 2 * insetX);
  const insetY = height * 0.08;
  const innerH = Math.max(0, height - 2 * insetY);
  const gap = innerH * 0.06;
  const rockerH = Math.max(0, (innerH - 2 * gap) / 3);
  const rockerW = innerW;
  const rockerX = width / 2 - rockerW / 2;
  const ys = [insetY, insetY + rockerH + gap, insetY + 2 * (rockerH + gap)];
  return (
    <g>
      {ys.map((y, i) => (
        <g key={i}>
          <Rect
            x={rockerX}
            y={y}
            width={rockerW}
            height={rockerH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
          <Line
            x1={rockerX}
            y1={y + rockerH / 2}
            x2={rockerX + rockerW}
            y2={y + rockerH / 2}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      ))}
    </g>
  );
}
